import { unassignSamples } from "./analystTaskApi";
import Spinner from "react-bootstrap/Spinner";
import { useState, useEffect, useMemo } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import { BsCircleFill, BsDownload } from "react-icons/bs";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import Dropdown from "react-bootstrap/Dropdown";
import { formatDate } from "../../../utility/Formatdate";
import "../../../styles/typography.css";
import SecondTab from "../../../components/organisms/secondTab";
import "./TaskStyles.css";
import {
  getMyAssignmentsData,
  updateMyAssignmentsData,
  exportReports,
} from "./analystTaskApi";
import AnalystTaskTabs from "./Components/AnalystTaskTabs";
import { toast, ToastContainer } from "react-toastify";
import ReusableModal from "../../../components/atoms/modal/ReusableModal";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const getStatusIcon = (row) => {
  return (
    <>
      {row.isGreaterThanTen && (
        <BsCircleFill
          color="#43B324"
          size={10}
          title="Due > 10"
          className="me-1"
        />
      )}
      {row.isGreaterThanFourteen && (
        <BsCircleFill
          color="#E13B3B"
          size={10}
          title="Due > 14"
          className="me-1"
        />
      )}
    </>
  );
};

const MyAssignment = ({ TabConfig }) => {
  const [loading, setLoading] = useState(false);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [statusDropdownRow, setStatusDropdownRow] = useState(null);
  // Show only 'Final' if lastStatus is 'Re-Assay 2', otherwise both options

  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [paginationData, setPaginationData] = useState({});

  const getNextStatusOptions = (row) => {
    if (row.lastStatus === "Re-Assay 2") {
      return ["Final"];
    }
    return ["Re Assay", "Final"];
  };
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const handleConfirmRemove = async () => {
    if (selectedRowIds.size === 0) return;
    const payload = Array.from(selectedRowIds);
    try {
      const res = await unassignSamples(payload);
      if (res) {
        toast.success("Samples unassigned successfully");
        await fetchData();
        setShowRemoveModal(false);
        setSelectedRowIds(new Set());
      } else {
        toast.error("Failed to unassign samples");
      }
    } catch (error) {
      toast.error("Failed to unassign samples");
    }
  };

  // Fetch data from API
  const fetchData = async (search, currentPage = 1, rowsPerPage = 25) => {
    setLoading(true);
    try {
      const payload = {
        companyId: selectedCompany ? Number(selectedCompany) : 0,
        productId: selectedProduct ? Number(selectedProduct) : 0,
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        status: selectedStatus || "",
      };
      const res = await getMyAssignmentsData(payload);
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setData(Array.isArray(res.results) ? res.results : []);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    selectedCompany,
    selectedProduct,
    currentPage,
    rowsPerPage,
    selectedStatus,
  ]);

  const handleStatusUpdate = async () => {
    if (selectedRowIds.size === 0) return;
    // Build payload array for selected rows
    const payload = Array.from(selectedRowIds).map((sampleId) => {
      const row = data.find((d) => d.sampleId === sampleId);
      return {
        sampleId: row.sampleId,
        assayResult: row.assayResult || "",
        reAssay1Result: row.reassay1Result || "",
        reAssay2Result: row.reassay2Result || "",
        comments: row.comments || "",
        nextStatus: row.nextStatus || "",
      };
    });
    try {
      const res = await updateMyAssignmentsData(payload);
      if (res) {
        toast.success("Assignments updated successfully");
        fetchData();
        setSelectedRowIds(new Set()); // Clear selections after successful update
      } else {
        toast.error("Failed to update assignments");
      }
    } catch (error) {
      toast.error("Failed to update assignments");
    }
  };

  const companyOptions = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      if (!map.has(row.companyId)) {
        map.set(row.companyId, row.companyName);
      }
    });
    return [
      { value: 0, label: "All" },
      ...Array.from(map.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [data]);

  const productOptions = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      if (!map.has(row.productTypeId)) {
        map.set(row.productTypeId, row.productName);
      }
    });
    return [
      { value: 0, label: "All" },
      ...Array.from(map.entries()).map(([value, label]) => ({ value, label })),
    ];
  }, [data]);

  const statusOptions = useMemo(
    () => [
      { value: "", label: "All" },
      ...Array.from(
        new Set(data.map((row) => row.lastStatus).filter(Boolean))
      ).map((s) => ({
        value: s,
        label: s,
      })),
    ],
    [data]
  );

  const handleDownloadTemplate = () => {
    if (!selectedTemplates.length) {
      toast.error("Please select at least one template to download");
      return;
    }
    selectedTemplates.forEach((templateValue) => {
      const selected = templateOptions.find(
        (opt) => opt.value === templateValue
      );
      if (!selected || !selected.file) return;
      const link = document.createElement("a");
      link.href = selected.file;
      link.setAttribute("download", selected.label);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };
  const handleExport = async () => {
    try {
      // Only use selectedRowIds for MyAssignment
      if (selectedRowIds.size === 0) {
        toast.error("Please select at least one sample to Export.");
        return;
      }

      const sampleIds = Array.from(selectedRowIds);
      const params = {
        SampleIds: sampleIds,
      };

      const response = await exportReports(params);

      // Handle file download (Excel)
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Report exported successfully.");
    } catch (error) {
      toast.error("Failed to export report.");
    }
  };


  const templateOptions = [
    {
      value: "BMD.xlsx",
      label: "BMD",
      file: require("../../../components/Assests/Template Files/BMD.xlsx"),
    },
    {
      value: "CTC.xlsx",
      label: "CTC",
      file: require("../../../components/Assests/Template Files/CTC.xlsx"),
    },
    {
      value: "DEC.xlsx",
      label: "DEC",
      file: require("../../../components/Assests/Template Files/DEC.xlsx"),
    },
    {
      value: "LAS.xlsx",
      label: "LAS",
      file: require("../../../components/Assests/Template Files/LAS.xlsx"),
    },
    {
      value: "LINC.xlsx",
      label: "LINC",
      file: require("../../../components/Assests/Template Files/LINC.xlsx"),
    },
    {
      value: "RAC.xlsx",
      label: "RAC",
      file: require("../../../components/Assests/Template Files/RAC.xlsx"),
    },
    {
      value: "ROB.xlsx",
      label: "ROB",
      file: require("../../../components/Assests/Template Files/ROB.xlsx"),
    },
    {
      value: "SMZ.xlsx",
      label: "SMZ",
      file: require("../../../components/Assests/Template Files/SMZ.xlsx"),
    },
    {
      value: "ZOA.xlsx",
      label: "ZOA",
      file: require("../../../components/Assests/Template Files/ZOA.xlsx"),
    },
  ];
  const paginatedData = useMemo(() => {
    // If API already paginates, use data directly
    return data;
  }, [data]);

  const isAllSelectedOnPage =
    paginatedData.length > 0 &&
    paginatedData.every((row) => selectedRowIds.has(row.sampleId));

  const toggleSelectAllOnPage = () => {
    setSelectedRowIds((prev) => {
      const copy = new Set(prev);
      if (!isAllSelectedOnPage) {
        paginatedData.forEach((row) => copy.add(row.sampleId));
      } else {
        paginatedData.forEach((row) => copy.delete(row.sampleId));
      }
      return copy;
    });
  };

  const hasValidEditableData = (row) => {
    if (!row || !row.nextStatus) return false;
    
    // Check if the editable field for this row's status has data
    if (row.lastStatus === "Assay") {
      return row.assayResult && row.assayResult.trim() !== "";
    } else if (row.lastStatus === "Re-Assay 1") {
      return row.reassay1Result && row.reassay1Result.trim() !== "";
    } else if (row.lastStatus === "Re-Assay 2") {
      return row.reassay2Result && row.reassay2Result.trim() !== "";
    }
    return false;
  };

  const getAutoNextStatus = (row, value) => {
    if (!row || value === undefined || value === null || value === "") {
      return null;
    }

    const numericValue = Number(value);
    const lowLimit = Number(row.lowLimit);
    const highLimit = Number(row.highLimit);

    if (
      Number.isNaN(numericValue) ||
      Number.isNaN(lowLimit) ||
      Number.isNaN(highLimit)
    ) {
      return null;
    }

    const isOutOfSpec = numericValue < lowLimit || numericValue > highLimit;

    if (row.lastStatus === "Assay") {
      return isOutOfSpec ? "Re Assay" : "Final";
    }

    if (row.lastStatus === "Re-Assay 1") {
      return isOutOfSpec ? "Re Assay" : "Final";
    }

    if (row.lastStatus === "Re-Assay 2") {
      return "Final";
    }

    return null;
  };

  const renderEditableCell = ({
    row,
    field,
    activeStatus,
    selectedRowIds,
    setData,
  }) => {
    const value = row[field];

    // If null/undefined/empty string, show a dash
    const displayValue =
      value === undefined || value === null || value === "" ? "-" : value;

    // Show editable input only when status matches & row is selected
    if (row.lastStatus === activeStatus && selectedRowIds.has(row.sampleId)) {
      return (
        <Form.Control
          size="sm"
          type="text"
          value={value ?? ""}
          onChange={(e) => {
            const inputValue = e.target.value;

            setData((prev) =>
              prev.map((r) =>
                r.sampleId === row.sampleId
                  ? {
                      ...r,
                      [field]: inputValue,
                    }
                  : r
              )
            );
          }}
          onBlur={(e) => {
            const inputValue = e.target.value;

            setData((prev) =>
              prev.map((r) =>
                r.sampleId === row.sampleId
                  ? {
                      ...r,
                      nextStatus:
                        getAutoNextStatus(r, inputValue) ?? "",
                    }
                  : r
              )
            );
          }}
        />
      );
    }

    // Otherwise just show text or dash
    return <span>{displayValue}</span>;
  };

  const columns = [
    {
      header: (
        <Form.Check
          type="checkbox"
          checked={isAllSelectedOnPage}
          onChange={toggleSelectAllOnPage}
          aria-label="Select all"
        />
      ),
      accessor: "checkbox",
      disableSort: true,
      cell: (row) => (
        <Form.Check
          type="checkbox"
          checked={selectedRowIds.has(row.sampleId)}
          onChange={() => {
            const newSet = new Set(selectedRowIds);
            if (newSet.has(row.sampleId)) newSet.delete(row.sampleId);
            else newSet.add(row.sampleId);
            setSelectedRowIds(newSet);
          }}
        />
      ),
    },
    { header: "Date", accessor: "date", cell: (row) => formatDate(row.date) },
    {
      header: "Sample ID",
      accessor: "sampleId",
      cell: (row) => (
        <span>
          {row.sampleId} {getStatusIcon(row)}
        </span>
      ),
    },
    { header: "Company", accessor: "companyName" },
    { header: "Product", accessor: "productName" },
    { header: "Unit", accessor: "unitName" },
    { header: "Exp Level", accessor: "expectedLevel" },
    {
      header: "Low Limit",
      accessor: "lowLimit",
      cell: (row) => row.lowLimit.toString(),
    },
    {
      header: "Up Limit",
      accessor: "highLimit",
      cell: (row) => row.highLimit.toString(),
    },
    { header: "Status", accessor: "lastStatus" },
    {
      header: "Assay Result",
      accessor: "assayResult",
      cell: (row) =>
        renderEditableCell({
          row,
          field: "assayResult",
          activeStatus: "Assay",
          selectedRowIds,
          setData,
        }),
    },
    {
      header: "Re-Assay1 Result",
      accessor: "reassay1Result",
      cell: (row) =>
        renderEditableCell({
          row,
          field: "reassay1Result",
          activeStatus: "Re-Assay 1",
          selectedRowIds,
          setData,
        }),
    },
    {
      header: "Re-Assay2 Result",
      accessor: "reassay2Result",
      cell: (row) =>
        renderEditableCell({
          row,
          field: "reassay2Result",
          activeStatus: "Re-Assay 2",
          selectedRowIds,
          setData,
        }),
    },
    { header: "Result", accessor: "result" },

    {
      header: "Action",
      accessor: "action",
      disableSort: true,
      cell: (row) => (
        <Dropdown style={{ marginLeft: 8 }}>
          <Dropdown.Toggle
            variant="info"
            style={{
              minWidth: 100,
              background: "#b2e6fa",
              color: "#222",
              fontWeight: 500,
              border: "1px solid #ccc",
            }}
            id={`dropdown-nextstatus-${row.sampleId}`}
            disabled={!selectedRowIds.has(row.sampleId)}
          >
            {row.nextStatus || "Select"}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {getNextStatusOptions(row).map((opt) => (
              <Dropdown.Item
                key={opt}
                active={row.nextStatus === opt}
                onClick={async () => {
                  setData((prev) =>
                    prev.map((r) =>
                      r.sampleId === row.sampleId
                        ? { ...r, nextStatus: opt }
                        : r
                    )
                  );
                  setStatusDropdownRow(row.sampleId);
                }}
              >
                {opt}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <SecondTab tabConfig={TabConfig} />
      <PageDescriptionBar items={[
        { type: "icon",  icon: "☑️", text: "Select – Choose samples to update in bulk" },
        { type: "icon",  icon: "🔄", text: "Update Status – Advance selected samples to the next workflow stage" },
        { type: "icon",  icon: "↩️", text: "Unassign – Return selected samples to the unassigned pool" },
        { type: "separator" },
        { type: "badge", badge: "warning", text: "Due > 10 days – Sample approaching deadline" },
        { type: "badge", badge: "danger",  text: "Due > 14 days – Sample past target completion date" },
      ]} />
      <div className="p-0 typography">
        <div
          className="d-flex shadow-lg justify-content-between align-items-center mb-3"
          style={{ background: "#fff", padding: "16px 9px 16px 12px" }}
        >
          <AnalystTaskTabs />
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 14, marginRight: 4 }}>Company</label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              style={{
                minWidth: 100,
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              {companyOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label style={{ fontSize: 14, marginLeft: 12, marginRight: 4 }}>
              Product
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              style={{
                minWidth: 100,
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              {productOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label style={{ fontSize: 14, marginLeft: 12, marginRight: 4 }}>
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                minWidth: 100,
                padding: "8px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div
              className="line-separator"
              style={{
                width: "1px",
                height: "46px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                margin: "0 10px",
              }}
            />
            <Dropdown
              show={templateDropdownOpen}
              onToggle={(isOpen, event, metadata) => {
                if (metadata && metadata.source === "rootClose") {
                  setTemplateDropdownOpen(false);
                } else {
                  setTemplateDropdownOpen(true);
                }
              }}
              style={{
                minWidth: 140,
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <Dropdown.Toggle
                variant="light"
                id="dropdown-template-select"
                onClick={() => setTemplateDropdownOpen((prev) => !prev)}
              >
                Select Template
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: 180 }}>
                <Dropdown.Item
                  as="div"
                  style={{ padding: "4px 12px", fontWeight: 600 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Form.Check
                    type="checkbox"
                    id="template-checkbox-all"
                    label="Select All"
                    checked={
                      selectedTemplates.length === templateOptions.length
                    }
                    onChange={(e) => {
                      setSelectedTemplates(
                        e.target.checked
                          ? templateOptions.map((opt) => opt.value)
                          : []
                      );
                    }}
                  />
                </Dropdown.Item>
                {templateOptions.map((opt) => (
                  <Dropdown.Item
                    key={opt.value}
                    as="div"
                    style={{ padding: "4px 12px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`template-checkbox-${opt.value}`}
                      label={opt.label}
                      checked={selectedTemplates.includes(opt.value)}
                      onChange={(e) => {
                        setSelectedTemplates((prev) =>
                          e.target.checked
                            ? [...prev, opt.value]
                            : prev.filter((v) => v !== opt.value)
                        );
                      }}
                    />
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="outlined"
              style={{
                padding: "8px 12px",
                border: 0,
                fontSize: 24,
              }}
              onClick={handleDownloadTemplate}
            >
              <BsDownload />
            </Button>
            <Button
              variant={selectedRowIds.size ? "active" : "disabled"}
              onClick={handleExport}
              disabled={!selectedRowIds.size}
            >
              Export
            </Button>
          </div>
        </div>
        <div
          className="shadow-lg mb-3"
          style={{
            background: "#fff",
            padding: "9px 20px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            variant={selectedRowIds.size ? "outlined" : "disabled"}
            disabled={!selectedRowIds.size}
            onClick={() => selectedRowIds.size && setShowRemoveModal(true)}
          >
            Remove
          </Button>
          <ReusableModal
            show={showRemoveModal}
            size="md"
            onHide={() => setShowRemoveModal(false)}
            title="Confirm Remove"
            buttons={[
              {
                text: "Cancel",
                variant: "outlined",
                onClick: () => setShowRemoveModal(false),
              },
              { text: "Ok", variant: "active", onClick: handleConfirmRemove },
            ]}
          >
            <p>
              Are you sure you want to remove the selected Assigned sample
              {selectedRowIds.size > 1 ? "s" : ""}?
            </p>
          </ReusableModal>
          <div
            className="line-separator"
            style={{
              width: "1px",
              height: "46px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              margin: "0 10px",
            }}
          />
          <Button
            variant={
              selectedRowIds.size &&
              Array.from(selectedRowIds).every((id) => {
                const row = data.find((d) => d.sampleId === id);
                return hasValidEditableData(row);
              })
                ? "outlined"
                : "disabled"
            }
            disabled={
              !selectedRowIds.size ||
              !Array.from(selectedRowIds).every((id) => {
                const row = data.find((d) => d.sampleId === id);
                return hasValidEditableData(row);
              })
            }
            title={
              !selectedRowIds.size
                ? "Please select at least one sample"
                : !Array.from(selectedRowIds).every((id) => {
                    const row = data.find((d) => d.sampleId === id);
                    return hasValidEditableData(row);
                  })
                ? "Please enter result data and select any action for all selected samples"
                : "Update"
            }
            onClick={handleStatusUpdate}
          >
            Update
          </Button>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spinner animation="border" /> Loading...
          </div>
        ) : (
          <Card>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <SimpleTable
                  tableClassName="tableStyle"
                  columns={columns}
                  data={paginatedData}
                  showPagination={true}
                  paginationData={paginationData}
                  searchValue={""}
                  fetchData={fetchData}
                />
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default MyAssignment;
